import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin, Switch } from "antd";
import { Icon } from "@iconify/react";
import Card from "../../Card";
import Button from "../../Button";
import TextField from "../../TextField";
import Table from "../../Table";
import { useMasterDataApi } from "../../../hooks/useMasterDataApi";

const normalizeField = (field, idx) => {
  const id = field?.id ?? field?.dropdown_id ?? field?.uuid ?? field?.key ?? idx;
  const key = field?.key ?? field?.field ?? field?.code ?? field?.name ?? "";
  const label = field?.label ?? field?.title ?? field?.displayName ?? key ?? "";
  return { ...field, id, key, label };
};

const normalizeOption = (opt, idx) => {
  const id = opt?.id ?? opt?.option_id ?? opt?.uuid ?? idx;
  const label = opt?.label ?? opt?.name ?? opt?.title ?? String(opt?.value ?? id);
  const value = opt?.value ?? opt?.key ?? opt?.code ?? label;
  const enabled = opt?.enabled ?? opt?.is_active ?? opt?.isActive ?? opt?.active;
  const display_order = opt?.display_order ?? opt?.displayOrder ?? opt?.order;
  return { ...opt, id, label, value, enabled, display_order };
};

const splitOptionsInput = (raw) => {
  const parts = String(raw || "")
    .split(/\n/g)
    .map((s) => s.trim())
    .filter(Boolean);
  const unique = [];
  const seen = new Set();
  for (const p of parts) {
    const k = p.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      unique.push(p);
    }
  }
  return unique;
};

const OptionsManager = ({ toast }) => {
  const {
    normalizeError,
    listDropdownFields,
    listDropdownOptions,
    createDropdownOptions,
    updateDropdownOption,
    deleteDropdownOption,
    setDropdownOptionStatus,
  } = useMasterDataApi();

  const [fields, setFields] = useState([]);
  const [selectedFieldKey, setSelectedFieldKey] = useState("");

  const [options, setOptions] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [deletingId, setDeletingId] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  const [editingOption, setEditingOption] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editDisplayOrder, setEditDisplayOrder] = useState("");
  const [updateOptionLoading, setUpdateOptionLoading] = useState(false);

  const [newOptionsRaw, setNewOptionsRaw] = useState("");
  const [newOptionsError, setNewOptionsError] = useState("");

  const normalizedFields = useMemo(
    () => fields.map((f, idx) => normalizeField(f, idx)).filter((f) => f.key),
    [fields]
  );

  const normalizedOptions = useMemo(
    () => options.map((o, idx) => normalizeOption(o, idx)),
    [options]
  );

  const optionLabelSet = useMemo(() => {
    const set = new Set();
    normalizedOptions.forEach((o) => {
      if (o?.label) set.add(String(o.label).trim().toLowerCase());
    });
    return set;
  }, [normalizedOptions]);

  const loadFields = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const data = await listDropdownFields();
      setFields(Array.isArray(data) ? data : []);
    } catch (err) {
      toast?.error(normalizeError(err));
      setFields([]);
    } finally {
      setFieldsLoading(false);
    }
  }, [listDropdownFields, normalizeError, toast]);

  const loadOptions = useCallback(
    async (fieldKey) => {
      if (!fieldKey) {
        setOptions([]);
        return;
      }
      setOptionsLoading(true);
      try {
        const data = await listDropdownOptions(fieldKey, { includeInactive });
        setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        toast?.error(normalizeError(err));
        setOptions([]);
      } finally {
        setOptionsLoading(false);
      }
    },
    [includeInactive, listDropdownOptions, normalizeError, toast]
  );

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  useEffect(() => {
    loadOptions(selectedFieldKey);
  }, [includeInactive, loadOptions, selectedFieldKey]);

  const validateNewOptions = () => {
    if (!selectedFieldKey) {
      setNewOptionsError("Select a dropdown field first.");
      return null;
    }
    const parsed = splitOptionsInput(newOptionsRaw);
    if (parsed.length === 0) {
      setNewOptionsError("Enter at least one option (one per line).");
      return null;
    }
    const duplicates = parsed.filter((p) => optionLabelSet.has(p.toLowerCase()));
    if (duplicates.length > 0) {
      setNewOptionsError(`Duplicate option(s): ${duplicates.join(", ")}`);
      return null;
    }
    setNewOptionsError("");
    return parsed;
  };

  const handleCreateOptions = async (e) => {
    e.preventDefault();
    const parsed = validateNewOptions();
    if (!parsed) return;

    setCreateLoading(true);
    try {
      await createDropdownOptions({ dropdownKey: selectedFieldKey, values: parsed });
      toast?.success("Options added successfully.");
      setNewOptionsRaw("");
      await loadOptions(selectedFieldKey);
    } catch (err) {
      toast?.error(normalizeError(err));
    } finally {
      setCreateLoading(false);
    }
  };

  const toggleOption = useCallback(
    async (option, nextEnabled) => {
      const value = option?.value ?? option?.label;
      const optionId = option?.id;
      if (!selectedFieldKey || (value === undefined && optionId === undefined)) return;
      const toggleKey = String(optionId ?? value);
      setTogglingIds((prev) => new Set([...prev, toggleKey]));
      try {
        await setDropdownOptionStatus({
          optionId,
          dropdownKey: selectedFieldKey,
          value,
          enabled: nextEnabled,
        });
        toast?.success("Option updated.");
        await loadOptions(selectedFieldKey);
      } catch (err) {
        toast?.error(normalizeError(err));
      } finally {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(toggleKey);
          return next;
        });
      }
    },
    [loadOptions, normalizeError, selectedFieldKey, setDropdownOptionStatus, toast]
  );

  const openEditOption = (opt) => {
    setEditingOption(opt);
    setEditValue(opt?.value ?? opt?.label ?? "");
    setEditActive(opt?.enabled !== false);
    setEditDisplayOrder(opt?.display_order != null ? String(opt.display_order) : "");
  };

  const handleUpdateOption = async (e) => {
    e.preventDefault();
    if (editingOption?.id == null) return;
    setUpdateOptionLoading(true);
    try {
      const payload = { value: editValue.trim(), is_active: editActive };
      const order = parseInt(editDisplayOrder, 10);
      if (!Number.isNaN(order)) payload.display_order = order;
      await updateDropdownOption(editingOption.id, payload);
      toast?.success("Option updated.");
      setEditingOption(null);
      await loadOptions(selectedFieldKey);
    } catch (err) {
      toast?.error(normalizeError(err));
    } finally {
      setUpdateOptionLoading(false);
    }
  };

  const handleDeleteOption = useCallback(
    async (opt) => {
      if (opt?.id == null) return;
      if (!window.confirm(`Delete option "${opt?.value ?? opt?.label}"?`)) return;
      setDeletingId(opt.id);
      try {
        await deleteDropdownOption(opt.id);
        toast?.success("Option deleted.");
        await loadOptions(selectedFieldKey);
      } catch (err) {
        toast?.error(normalizeError(err));
      } finally {
        setDeletingId(null);
      }
    },
    [deleteDropdownOption, loadOptions, normalizeError, selectedFieldKey, toast]
  );

  const columns = useMemo(
    () => [
      { title: "Option", dataIndex: "label", key: "label" },
      {
        title: "Enabled",
        dataIndex: "enabled",
        key: "enabled",
        render: (_value, record) => {
          const current =
            record.enabled === undefined || record.enabled === null
              ? true
              : Boolean(record.enabled);
          const disabledKey = String(record.id ?? record.value ?? record.label);
          const disabled = !selectedFieldKey || togglingIds.has(disabledKey);
          return (
            <Switch
              checked={current}
              disabled={disabled}
              onChange={(checked) => toggleOption(record, checked)}
            />
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              variant="secondary"
              onClick={() => openEditOption(record)}
              startIcon={<Icon icon="lucide:edit" className="h-3.5 w-3.5" />}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={() => handleDeleteOption(record)}
              disabled={deletingId === record.id}
              startIcon={<Icon icon="lucide:trash-2" className="h-3.5 w-3.5" />}
            >
              {deletingId === record.id ? "..." : "Delete"}
            </Button>
          </div>
        ),
      },
    ],
    [selectedFieldKey, toggleOption, togglingIds, deletingId, handleDeleteOption]
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]">
      <Card header="Add dropdown options">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-700 text-left">
              Dropdown Field <span className="text-red-500">*</span>
            </div>
            <Spin spinning={fieldsLoading}>
              <Select
                value={selectedFieldKey || undefined}
                placeholder="Select a dropdown field"
                onChange={(val) => setSelectedFieldKey(val)}
                options={normalizedFields.map((f) => ({
                  value: f.key,
                  label: `${f.label} (${f.key})`,
                }))}
                className="w-full"
                showSearch
                optionFilterProp="label"
              />
            </Spin>
          </div>

          <form className="space-y-4" onSubmit={handleCreateOptions}>
            <TextField
              label="New Options"
              name="newOptions"
              value={newOptionsRaw}
              onChange={(e) => setNewOptionsRaw(e.target.value)}
              placeholder={"Enter options, one per line\nExample:\nOption A\nOption B"}
              multiline
              rows={8}
              required
              error={Boolean(newOptionsError)}
              className="min-h-[12rem] whitespace-pre overflow-x-auto font-mono text-[13px] leading-normal"
              helperText={
                newOptionsError ||
                "Tip: one option per line (press Enter between options). Only real line breaks split options—long lines scroll horizontally and do not create extra options. Duplicates are blocked."
              }
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" variant="primary" loading={createLoading}>
                Add Options
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={createLoading}
                onClick={() => {
                  setNewOptionsRaw("");
                  setNewOptionsError("");
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        </div>
      </Card>

      <Card
        header={
          <div className="flex items-center justify-between w-full">
            <span>Existing options</span>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 text-sm text-slate-600">
                <span>Include inactive</span>
                <Switch
                  checked={includeInactive}
                  onChange={(checked) => setIncludeInactive(checked)}
                  disabled={!selectedFieldKey}
                />
              </div>
              <Button
                size="small"
                variant="secondary"
                onClick={() => loadOptions(selectedFieldKey)}
                disabled={optionsLoading || !selectedFieldKey}
              >
                Refresh
              </Button>
            </div>
          </div>
        }
      >
        <div className="sm:hidden mb-3 flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm text-slate-700">Include inactive</span>
          <Switch
            checked={includeInactive}
            onChange={(checked) => setIncludeInactive(checked)}
            disabled={!selectedFieldKey}
          />
        </div>
        <Spin spinning={optionsLoading}>
          <Table
            columns={columns}
            data={normalizedOptions}
            rowKey="id"
            emptyMessage={
              selectedFieldKey
                ? "No options found for this field."
                : "Select a field to view options."
            }
          />
        </Spin>
      </Card>

      <Modal
        title="Edit option"
        open={Boolean(editingOption)}
        onCancel={() => setEditingOption(null)}
        footer={null}
        destroyOnClose
      >
        {editingOption && (
          <form className="space-y-4" onSubmit={handleUpdateOption}>
            <TextField
              label="Value"
              name="editValue"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Option value"
              required
            />
            <TextField
              label="Display order"
              name="editDisplayOrder"
              type="number"
              value={editDisplayOrder}
              onChange={(e) => setEditDisplayOrder(e.target.value)}
              placeholder="e.g., 1"
            />
            <div className="flex items-center gap-2">
              <Switch checked={editActive} onChange={setEditActive} />
              <span className="text-sm text-slate-700">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditingOption(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={updateOptionLoading}>
                Save
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default OptionsManager;

